import React, { useEffect, useRef } from 'react';
import CaugthAFish from '../../assets/images/Caught_A_Fish.jpg';
import DayFishing from '../../assets/images/Day_Fishing.jpg';
import EveningFishing from '../../assets/images/Evening_Fishing.jpg';
import FishingStand from '../../assets/images/FishingStand.jpg';

const AboutSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  // Scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    
    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => observer.observe(el));
    
    return () => {
      elements?.forEach(el => observer.unobserve(el));
    };
  }, []);
  
  return (
    <section id="about" className="py-20 bg-background relative overflow-hidden" ref={sectionRef}>
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
        <svg viewBox="0 0 100 100" className="absolute top-0 left-0 w-1/2 text-primary animate-pulse" style={{ animationDuration: '10s' }}>
          <circle cx="50" cy="50" r="40" fill="currentColor" />
        </svg>
        <svg viewBox="0 0 100 100" className="absolute bottom-0 right-0 w-1/2 text-secondary animate-pulse" style={{ animationDuration: '15s' }}>
          <circle cx="50" cy="50" r="40" fill="currentColor" />
        </svg>
      </div>
      
      {/* Wave pattern */}
      <div className="absolute top-0 left-0 w-full h-10 -mt-10 wave-animation" aria-hidden="true"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Image collage */}
          <div className="w-full lg:w-1/2 relative animate-on-scroll opacity-0" style={{ transitionDelay: '0.2s' }}>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative h-60 rounded-lg overflow-hidden shadow-xl transform rotate-1 hover:rotate-0 transition-all duration-300">
                <img 
                  src={CaugthAFish} 
                  alt="Professional fishing" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative h-60 rounded-lg overflow-hidden shadow-xl transform -rotate-1 hover:rotate-0 transition-all duration-300">
                <img 
                  src={DayFishing} 
                  alt="Fishing equipment" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative h-60 rounded-lg overflow-hidden shadow-xl transform -rotate-2 hover:rotate-0 transition-all duration-300">
                <img 
                  src={EveningFishing} 
                  alt="Fishing equipment" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative h-60 rounded-lg overflow-hidden shadow-xl transform rotate-2 hover:rotate-0 transition-all duration-300 flex items-center justify-center">
                <img 
                  src={FishingStand} 
                  alt="Mad Anglers Logo" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="w-full lg:w-1/2">
            <div className="mb-6 animate-on-scroll opacity-0">
              <span className="text-primary font-heading text-sm font-semibold uppercase tracking-wider">About Us</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mt-2 mb-4">
                <span className="text-gradient">Welcome</span> to MAD Anglers
              </h2>
              <div className="w-20 h-1 bg-primary rounded mb-6"></div>
            </div>
            
            <div className="prose prose-lg max-w-none text-text animate-on-scroll opacity-0" style={{ transitionDelay: '0.4s' }}>
              <p>
                  Feel free to browse our custom-made fishing accessories and high-quality gear — everything you need for that dream catch by the water. 
              </p>
              <br/>
              <p>
              Whether you're a weekend warrior or a seasoned pro, we’ve got something for you. From our own hand-built fishing stands, we supply gear that works — no gimmicks, just the good stuff.
              </p>
              <p>
              Need it at your door?
              </p>
              <p>
              No problem. We deliver from our base to wherever the fish are biting. And if you’ve got a unique idea or a custom project in mind, we’re also open to private builds and one-off jobs — just reach out and let’s talk.
              </p>
              <br />
              <p>
              Local. Reliable. Built for anglers, by anglers.
              That’s MAD Anglers.
              </p>
            </div>
            
            {/* Features */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="flex items-start gap-3 animate-on-scroll opacity-0" style={{ transitionDelay: '0.6s' }}>
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg">Modular Design</h3>
                  <p className="text-gray-600 text-sm">Field-tested</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 animate-on-scroll opacity-0" style={{ transitionDelay: '0.7s' }}>
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg">Quality Materials</h3>
                  <p className="text-gray-600 text-sm">Mild Steel, 0.25% carbon</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection; 